import React, { forwardRef, Suspense, useEffect, useRef, useState } from 'react';
import * as THREE from 'three'; 
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei'; // For controlling camera rotation
import CubeComponent from './CubeComponent';
import CubeLoader from './CubeLoader';
import Spinner from '../../Loaders/Spinner';

const Cube = forwardRef(({ actionImage, playerImage, canvasRef }, containerRef) => {
  const controlsRef = useRef();
  // const canvasRef = useRef();

  const [textures, setTextures] = useState({ playerImageTexture: null, actionImageTexture: null });

  const handleTouchStart = () => {
    if (controlsRef.current) {
      controlsRef.current.enableRotate = false; // Disable rotation on touch start
    }
  };

  const handleTouchEnd = () => {
    if (controlsRef.current) {
      controlsRef.current.enableRotate = true; // Re-enable rotation on touch end
    }
  };

  useEffect(() => {
    const loader = new THREE.TextureLoader();

    if(!playerImage || !actionImage) return;

    const loadTextures = async () => {
      try {
        const playerImageTexture = await loader.loadAsync(playerImage);
        const actionImageTexture = await loader.loadAsync(actionImage);

        // Configure texture filtering for higher quality
        playerImageTexture.minFilter = THREE.LinearMipMapLinearFilter;
        playerImageTexture.magFilter = THREE.LinearFilter;
        playerImageTexture.anisotropy = 16;

        actionImageTexture.minFilter = THREE.LinearMipMapLinearFilter;
        actionImageTexture.magFilter = THREE.LinearFilter;
        actionImageTexture.anisotropy = 16;

        setTextures({ playerImageTexture, actionImageTexture });
      } catch (error) {
        console.error('Error loading textures:', error);
      }
    };

    loadTextures();
  }, [actionImage, playerImage]);

  useEffect(() => {
    const handleResize = () => {
      if(!containerRef?.current) return;

      const canvas = canvasRef?.current;
      if (canvas) {
        const width = containerRef?.current?.clientWidth;
        // const height = containerRef?.current?.clientHeight;
        canvas.style.width = `${width - 20}px`;
        // canvas.style.height = `${height}px`;
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Call once to set initial size

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [containerRef]);

  // Show the loader while textures are loading
  if (!textures.playerImageTexture || !textures.actionImageTexture) {
      return <Spinner />;
  }

  return (
    <Canvas 
      gl={{ powerPreference: "high-performance", preserveDrawingBuffer: true, antialias: true, alpha: true, version: 1 }} 
      camera={{ position: [0, 0, 5], fov: 45 }} style={{ width: '100%', height: '100%', background: 'none' }} 
      ref={canvasRef}
      id='cube-canvas-ref-id-1'
    >
      {/* Ambient Lightning */}
      <ambientLight intensity={0.4} />
      {/* Directional Lightning */}
      <directionalLight position={[5, 1, 1]} intensity={1} />

      {/* Cube Component */}
      <Suspense fallback={<CubeLoader />}>
        <CubeComponent
          playerImage={textures.playerImageTexture}
          actionImage={textures.actionImageTexture}
        />
      </Suspense>

      {/* Orbit Controle */}
      <OrbitControls
        ref={controlsRef}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        enableZoom={false} // Allow zooming
        enableRotate={false} // Enable rotation by default
      />
    </Canvas>
  );
});

Cube.displayName = 'Cube';

export default React.memo(Cube);